import { AVATAR_EXPRESSIONS, AVATAR_FAMILIES, AVATAR_MATERIALS, AVATAR_PALETTES } from '@algoboo/schema/api'
import type { AgentAvatar, AvatarExpression, AvatarFamily, AvatarMaterial, AvatarPalette } from '@algoboo/schema/api'

export interface FamilyInfo {
  label: string
  blurb: string
  /** Every style shares the reference ghost's exposed face and dark oval eyes. */
  face: 'jelly'
}

export const FAMILY_INFO: Record<AvatarFamily, FamilyInfo> = {
  mochi: { label: 'Mochi', blurb: 'A little unadorned jelly ghost', face: 'jelly' },
  lumi: { label: 'Lumi', blurb: 'A hooded ghost with pearl ear charms', face: 'jelly' },
  nova: { label: 'Nova', blurb: 'A ghost in a clear bubble helmet', face: 'jelly' },
  bop: { label: 'Bop', blurb: 'A ghost with a listening headset', face: 'jelly' },
  nimbus: { label: 'Nimbus', blurb: 'A ghost with soft ear warmers', face: 'jelly' },
  pico: { label: 'Pico', blurb: 'A little ghost with a pearl collar', face: 'jelly' },
  aura: { label: 'Aura', blurb: 'A luminous ghost with an orbit collar', face: 'jelly' },
  mellow: { label: 'Mellow', blurb: 'A cozy ghost in a knitted beanie', face: 'jelly' },
  comet: { label: 'Comet', blurb: 'A wandering ghost in a soft scarf', face: 'jelly' },
  glim: { label: 'Glim', blurb: 'A little ghost in a tailored hood', face: 'jelly' },
}

export interface PartOption {
  id: string
  label: string
}

export const HEADWEAR_OPTIONS: PartOption[] = [
  { id: 'hood', label: 'Hood' },
  { id: 'beanie', label: 'Beanie' },
  { id: 'bucket-hat', label: 'Bucket hat' },
  { id: 'cap', label: 'Cap' },
  { id: 'visor', label: 'Visor' },
  { id: 'headset', label: 'Headset' },
  { id: 'headband', label: 'Headband' },
  { id: 'goggles', label: 'Goggles' },
  { id: 'antenna', label: 'Antenna' },
  { id: 'ear-warmers', label: 'Ear warmers' },
]

export const OUTFIT_OPTIONS: PartOption[] = [
  { id: 'scarf', label: 'Scarf' },
  { id: 'cape', label: 'Cape' },
  { id: 'hoodie', label: 'Hoodie' },
  { id: 'puffer', label: 'Puffer' },
  { id: 'cardigan', label: 'Cardigan' },
  { id: 'waistcoat', label: 'Waistcoat' },
  { id: 'windbreaker', label: 'Windbreaker' },
  { id: 'racing-jacket', label: 'Racing jacket' },
  { id: 'collar', label: 'Orbit collar' },
]

export const LOADOUT_OPTIONS: PartOption[] = [
  { id: 'pouch', label: 'Pouch' },
  { id: 'satchel', label: 'Satchel' },
  { id: 'tablet', label: 'Tablet' },
  { id: 'ledger-book', label: 'Ledger' },
  { id: 'compass', label: 'Compass' },
  { id: 'magnifier', label: 'Magnifier' },
  { id: 'lightning-bolt', label: 'Bolt' },
  { id: 'wrist-display', label: 'Wrist display' },
  { id: 'backpack', label: 'Backpack' },
  { id: 'star', label: 'Star' },
]

export const EXPRESSION_LABELS: Record<AvatarExpression, string> = {
  smile: 'Smile',
  happy: 'Happy',
  sleepy: 'Sleepy',
  curious: 'Curious',
  focused: 'Focused',
  wink: 'Wink',
}

export const MATERIAL_LABELS: Record<AvatarMaterial, string> = {
  jelly: 'Jelly',
  matte: 'Soft matte',
  frosted: 'Frosted',
  velvet: 'Velvet',
  quilted: 'Quilted',
}

// Lead with the two reference ghosts; wire ids stay stable for saved designs.
export const FAMILIES: readonly AvatarFamily[] = ['glim', 'aura', ...AVATAR_FAMILIES.filter((f) => f !== 'glim' && f !== 'aura')]
export const PALETTE_IDS = AVATAR_PALETTES
export const EXPRESSIONS = AVATAR_EXPRESSIONS
export const MATERIALS = AVATAR_MATERIALS

/** Map free-form slugs from the API onto parts this renderer knows. */
export function resolvePart(kind: 'headwear' | 'outfit' | 'loadout', slug: string | undefined): string | undefined {
  if (!slug) return undefined
  const list = kind === 'headwear' ? HEADWEAR_OPTIONS : kind === 'outfit' ? OUTFIT_OPTIONS : LOADOUT_OPTIONS
  if (list.some((o) => o.id === slug)) return slug
  const s = slug.toLowerCase()
  const rules: [RegExp, string][] =
    kind === 'headwear'
      ? [[/hood/, 'hood'], [/beanie|knit|toque/, 'beanie'], [/bucket|hat/, 'bucket-hat'], [/cap/, 'cap'], [/visor|brim/, 'visor'], [/head(set|phone)|comms|mic/, 'headset'], [/band/, 'headband'], [/goggle|glass|lens/, 'goggles'], [/antenna|stalk/, 'antenna'], [/ear/, 'ear-warmers']]
      : kind === 'outfit'
        ? [[/scarf|shawl/, 'scarf'], [/cape|cloak/, 'cape'], [/hood/, 'hoodie'], [/puff|parka|down/, 'puffer'], [/cardi|knit|sweater/, 'cardigan'], [/vest|waist/, 'waistcoat'], [/wind|rain|shell/, 'windbreaker'], [/rac|jacket|bomber/, 'racing-jacket'], [/collar|ring|band/, 'collar']]
        : [[/pouch|purse/, 'pouch'], [/satchel|bag|messenger/, 'satchel'], [/tablet|pad|screen/, 'tablet'], [/book|ledger|notebook/, 'ledger-book'], [/compass|map/, 'compass'], [/magni|lens|loupe/, 'magnifier'], [/bolt|lightning|spark/, 'lightning-bolt'], [/wrist|watch|display/, 'wrist-display'], [/pack/, 'backpack'], [/star/, 'star']]
  for (const [re, id] of rules) if (re.test(s)) return id
  return undefined
}

function fnv1a(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** Small deterministic PRNG (mulberry32). */
function rng(seed: number): () => number {
  let a = seed
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function pick<T>(r: () => number, list: readonly T[]): T {
  return list[Math.floor(r() * list.length) % list.length]!
}

/**
 * Every agent has a face: hash the handle into a stable mascot config. An
 * explicit `avatar` from the API always wins; this is the fallback.
 */
export function defaultAvatarFor(handle: string): AgentAvatar {
  const r = rng(fnv1a(`rhbot:${handle}`))
  const family: AvatarFamily = pick(r, AVATAR_FAMILIES)
  const palette: AvatarPalette = pick(r, AVATAR_PALETTES)
  const expression: AvatarExpression = pick(r, AVATAR_EXPRESSIONS)
  const material: AvatarMaterial = pick(r, ['jelly', 'jelly', 'frosted', 'jelly'] as const)
  const avatar: AgentAvatar = { family, palette, expression, material }
  if (r() < 0.55) avatar.headwear = pick(r, HEADWEAR_OPTIONS).id
  if (r() < 0.5) avatar.outfit = pick(r, OUTFIT_OPTIONS).id
  if (r() < 0.45) avatar.loadout = pick(r, LOADOUT_OPTIONS).id
  return avatar
}

export function describeAvatar(a: AgentAvatar): string {
  const parts = [PALETTE_LABEL(a.palette), FAMILY_INFO[a.family].label]
  return `${parts.join(' ')} mascot`
}

function PALETTE_LABEL(p: AvatarPalette): string {
  return p.charAt(0).toUpperCase() + p.slice(1)
}
